<template>
  <div class="border-container-unique38 mb-4">
    <div class="admin-content-layout">
      <div
        class="admin-main-content"
        style="
          padding-top: 0px;
          max-width: 100%;
          padding-right: 0px;
          overflow-x: scroll;
        "
      >
        <!-- Header Section -->
        <div class="d-flex justify-space-between align-items-center mb-4">
          <div class="admin-header">
            <h1>Quote Logging & Retrieval</h1>
          </div>
        </div>

        <!-- Filter Section -->
        <div class="filter-section mb-4">
          <div class="d-flex justify-space-between align-items-center">
            <div class="d-flex ga-3">
              <!-- Search Input -->
              <div class="search-container">
                <div class="search-input">
                  <SearchIcon />
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search quote ID or email"
                    class="search-field"
                    @input="handleSearch"
                  />
                </div>
              </div>

              <!-- Date Filter -->
              <SingleSelect
                v-model="selectedDate"
                :options="dateOptions"
                prefix="Date: "
                placeholder="Select date range"
              />

              <!-- Region Filter -->
              <SingleSelect
                v-model="selectedRegion"
                :options="regionOptions"
                prefix="Region: "
                placeholder="Select region"
              />
            </div>

            <!-- Export Button -->
            <ActionButton
              variant="tertiary"
              size="medium"
              :loading="exportLoading"
              loading-text="Exporting..."
              @click="handleExportCSV"
            >
              Export CSV.
            </ActionButton>
          </div>

          <!-- Custom Date Range Picker -->
          <div
            v-if="selectedDate === 'custom'"
            class="mt-3"
            style="max-width: 450px"
          >
            <DateRangePicker
              v-model="customDateRange"
              @apply="handleCustomDateApply"
            />
          </div>
        </div>

        <!-- Quote Log Table Section -->
        <div class="quote-log-container">
          <TableData
            :headers="tableHeaders"
            :items="quoteLogs"
            :loading="loading"
            :total-pages="totalPages"
            :current-page="currentPage"
            :show-data-info="true"
            :total-rows="quoteLogsData?.total_rows || 0"
            :current-limit="itemsPerPage"
            @update:current-page="updatePage"
            @sort="handleSort"
            variant="rounded"
          >
            <template #cell-quotes_code="{ value, item }">
              <button
                v-if="value"
                type="button"
                class="quote-id-link"
                @click="openQuotePreview(item)"
              >
                {{ value }}
              </button>
              <span v-else>-</span>
            </template>
            <template #cell-email="{ value, item }">
              <span class="email-text">
                {{ value || "-" }}
                <span v-if="item.user_state" class="user-state">
                  · {{ item.user_state }}
                </span>
              </span>
            </template>
            <template #cell-quotes_total="{ value, item }">
              <span class="total-amount">
                {{ formatCurrency(value, item.region) }}
              </span>
            </template>
          </TableData>
        </div>
      </div>
    </div>

    <v-dialog
      v-model="previewModalOpen"
      class="quote-preview-dialog"
      max-width="794"
      scrollable
      @update:model-value="handlePreviewModalUpdate"
    >
      <div class="quote-preview-modal">
        <button
          type="button"
          class="quote-preview-close"
          aria-label="Close quote preview"
          @click="closeQuotePreview"
        >
          <v-icon size="24">mdi-close</v-icon>
        </button>

        <div v-if="previewLoading" class="quote-preview-state">
          <div class="spinner"></div>
          <p>Loading quote preview...</p>
        </div>

        <div v-else-if="previewError" class="quote-preview-state">
          <p>{{ previewError }}</p>
          <ActionButton variant="tertiary" size="medium" @click="retryPreview">
            Retry
          </ActionButton>
        </div>

        <div
          v-else-if="previewPages.length"
          class="quote-preview-document"
          :aria-label="`Quote preview ${previewQuoteCode}`"
        >
          <img
            v-for="page in previewPages"
            :key="page.pageNumber"
            class="quote-preview-page"
            :src="page.url"
            :alt="`Quote preview page ${page.pageNumber}`"
          />
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import TableData from "@/components/common/TableData.vue";
import DateRangePicker from "@/components/common/DateRangePicker.vue";
import makercartLogService from "@/services/makercartLogService";
import { SearchIcon } from "@/components/icons";
import ActionButton from "@/components/common/ActionButton.vue";

const searchQuery = ref("");

const dateOptions = [
  { value: "all", text: "<strong>All Dates</strong>" },
  { value: "daily", text: "<strong>Daily</strong>" },
  { value: "weekly", text: "<strong>Weekly</strong>" },
  { value: "monthly", text: "<strong>Monthly</strong>" },
  { value: "custom", text: "<strong>Custom Date</strong>" },
];

const regionOptions = [
  { value: "all", text: "<strong>All Regions</strong>" },
  { value: "US", text: "<strong>US</strong>" },
  { value: "SG", text: "<strong>SG</strong>" },
];

const selectedDate = ref("all");
const selectedRegion = ref("all");
const customDateRange = ref({ from: null, to: null });

const quoteLogsData = ref(null);
const loading = ref(false);
const exportLoading = ref(false);
const previewModalOpen = ref(false);
const previewLoading = ref(false);
const previewError = ref("");
const previewPages = ref([]);
const previewQuoteCode = ref("");
let previewRequestId = 0;
let pdfjsLoadPromise = null;

const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(1);

const tableHeaders = [
  { text: "Quote ID", value: "quotes_code", sortable: true },
  { text: "Email", value: "email", sortable: true },
  { text: "Date", value: "date", sortable: true },
  { text: "Timestamp", value: "time", sortable: true },
  { text: "Region", value: "region", sortable: true },
  { text: "Total", value: "quotes_total", sortable: true },
];

const quoteLogs = computed(() => {
  if (!quoteLogsData.value || !quoteLogsData.value.rows) return [];

  return quoteLogsData.value.rows.map((log) => ({
    id: log.id,
    quotes_code: log.quotes_code,
    email: log.email,
    user_state: log.user_state,
    date: log.date,
    time: log.time,
    region: log.region,
    quotes_total: log.quotes_total,
    created_at: log.created_at,
    updated_at: log.updated_at,
  }));
});

const formatCurrency = (amount, region) => {
  if (!amount && amount !== 0) return "-";
  const currencySymbols = {
    US: "$",
    SG: "$",
    JP: "¥",
  };
  const symbol = currencySymbols[region?.toUpperCase()] || "$";
  return `${symbol}${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getDateRange = () => {
  const now = new Date();
  let to = now.toISOString().split("T")[0];
  let from;

  switch (selectedDate.value) {
    case "daily":
      from = to;
      to = null;
      break;
    case "weekly": {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      from = weekAgo.toISOString().split("T")[0];
      break;
    }
    case "monthly": {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      from = monthAgo.toISOString().split("T")[0];
      break;
    }
    case "custom":
      if (customDateRange.value.from && customDateRange.value.to) {
        from = customDateRange.value.from;
        to = customDateRange.value.to;
      } else {
        // If custom is selected but no dates chosen, return null to not filter
        return { from: null, to: null };
      }
      break;
    default:
      from = to;
  }

  return { from, to };
};

const buildRequestFilters = () => {
  const filters = {};

  if (selectedRegion.value !== "all") {
    filters.region = selectedRegion.value.toUpperCase();
  }

  if (selectedDate.value !== "all") {
    const { from, to } = getDateRange();
    if (from) filters.from = from;
    if (to) filters.to = to;
  }

  if (searchQuery.value.trim()) {
    filters.quotes_code = searchQuery.value.trim();
  }

  return filters;
};

const fetchQuoteLogs = async (filters) => {
  try {
    loading.value = true;
    const response = await makercartLogService.getQuoteLog({
      page: currentPage.value,
      limit: itemsPerPage.value,
      sort_by: "created_at",
      order: "desc",
      ...filters,
    });
    if (response.success) {
      quoteLogsData.value = response.data;
      totalPages.value = response.data.total_page || 1;
    }
  } catch (error) {
    console.error("Error fetching quote logs:", error);
  } finally {
    loading.value = false;
  }
};

const updatePage = (page) => {
  currentPage.value = page;
  fetchQuoteLogs(buildRequestFilters());
};

const handleSort = (sortData) => {
  console.log("Sort data:", sortData);
};

let searchTimeout = null;
const handleSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  searchTimeout = setTimeout(() => {
    currentPage.value = 1;
    fetchQuoteLogs(buildRequestFilters());
  }, 500);
};

const handleCustomDateApply = (dateRange) => {
  customDateRange.value = dateRange;
  currentPage.value = 1;
  fetchQuoteLogs(buildRequestFilters());
};

const handleExportCSV = async () => {
  if (exportLoading.value) return;
  try {
    exportLoading.value = true;
    const filters = buildRequestFilters();

    const blob = await makercartLogService.exportQuotesLogs(filters);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const regionStr =
      selectedRegion.value !== "all" ? `_${selectedRegion.value}` : "";
    const dateRangeStr =
      selectedDate.value !== "daily" ? `_${selectedDate.value}` : "";
    const searchStr = searchQuery.value.trim()
      ? `_search_${searchQuery.value.trim()}`
      : "";

    link.download = `quotes_logs_${dateStr}${regionStr}${dateRangeStr}${searchStr}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error exporting CSV:", error);
  } finally {
    exportLoading.value = false;
  }
};

const revokePreviewUrls = () => {
  previewPages.value.forEach((page) => {
    window.URL.revokeObjectURL(page.url);
  });
  previewPages.value = [];
};

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Unable to render quote preview page."));
        return;
      }

      resolve(blob);
    }, "image/png");
  });

const loadPdfJs = async () => {
  if (!pdfjsLoadPromise) {
    pdfjsLoadPromise = Promise.all([
      import("pdfjs-dist/legacy/build/pdf"),
      import("pdfjs-dist/legacy/build/pdf.worker.entry"),
    ]).then(([pdfjsLib, pdfjsWorker]) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        pdfjsWorker.default || pdfjsWorker;
      return pdfjsLib;
    });
  }

  return pdfjsLoadPromise;
};

const renderPdfPreview = async (blob, requestId) => {
  const pdfjsLib = await loadPdfJs();
  const data = new Uint8Array(await blob.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({ data, isEvalSupported: false })
    .promise;
  const renderedPages = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    if (requestId !== previewRequestId) return [];

    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: context,
      viewport,
    }).promise;

    const pageBlob = await canvasToBlob(canvas);
    renderedPages.push({
      pageNumber,
      url: window.URL.createObjectURL(pageBlob),
    });
  }

  return renderedPages;
};

const openQuotePreview = async (item) => {
  const quoteCode = item?.quotes_code;

  if (!quoteCode || previewLoading.value) return;

  const requestId = (previewRequestId += 1);

  previewModalOpen.value = true;
  previewLoading.value = true;
  previewError.value = "";
  previewQuoteCode.value = quoteCode;
  revokePreviewUrls();

  try {
    const blob = await makercartLogService.previewQuote(quoteCode);
    const renderedPages = await renderPdfPreview(blob, requestId);

    if (requestId === previewRequestId) {
      previewPages.value = renderedPages;
    } else {
      renderedPages.forEach((page) => window.URL.revokeObjectURL(page.url));
    }
  } catch (error) {
    previewError.value = "Quote preview could not be loaded. Please try again.";
    console.error("Error fetching quote preview:", error);
  } finally {
    if (requestId === previewRequestId) {
      previewLoading.value = false;
    }
  }
};

const closeQuotePreview = () => {
  previewRequestId += 1;
  previewModalOpen.value = false;
  previewLoading.value = false;
  previewError.value = "";
  previewQuoteCode.value = "";
  revokePreviewUrls();
};

const handlePreviewModalUpdate = (isOpen) => {
  if (!isOpen) {
    closeQuotePreview();
  }
};

const retryPreview = () => {
  openQuotePreview({ quotes_code: previewQuoteCode.value });
};

watch([selectedDate, selectedRegion], () => {
  // Reset custom date range if user switches away from custom
  if (selectedDate.value !== "custom") {
    customDateRange.value = { from: null, to: null };
  }
  currentPage.value = 1;
  fetchQuoteLogs(buildRequestFilters());
});

onMounted(() => {
  fetchQuoteLogs(buildRequestFilters());
});

onBeforeUnmount(() => {
  previewRequestId += 1;
  revokePreviewUrls();
});
</script>

<style scoped>
/* Header Styles */
.admin-header h1 {
  font-size: 36px;
  font-weight: 700;
  color: var(--primary-blue);
  margin: 0;
  font-family: "League Spartan", sans-serif;
}

/* Filter Section */
.filter-section {
  background: white;
  border: 1px solid #d8dae5;
  border-radius: 16px;
  padding: 16px;
}

.search-container {
  width: 315px;
}

.search-input {
  position: relative;
  height: 56px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  padding: 4px 16px;
  gap: 16px;
}

.search-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.search-field {
  flex: 1;
  border: none;
  outline: none;
  font-size: var(--fs-medium);
  font-weight: 500;
  color: #98a2b3;
  font-family: "League Spartan", sans-serif;
}

.search-field::placeholder {
  color: #98a2b3;
}

.search-field:focus {
  color: var(--primary-blue);
}

.export-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f2f3fb;
  border-radius: 12px;
  font-size: var(--fs-medium);
  font-weight: 700;
  color: var(--primary-blue);
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
}

/* Quote Log Container */
.quote-log-container {
  background: #f2f3fb;
  padding: 32px;
  border-radius: 16px;
  box-shadow:
    0px 1px 3px 0px rgba(16, 24, 40, 0.1),
    0px 1px 2px 0px rgba(16, 24, 40, 0.06);
}

/* Table Cell Styles */
.quote-id-link {
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--primary-blue);
  text-decoration: underline;
  text-underline-offset: 30%;
  font-weight: 500;
  font-family: "League Spartan", sans-serif;
  cursor: pointer;
}

.quote-id-link:focus-visible {
  outline: 2px solid var(--primary-blue);
  outline-offset: 3px;
  border-radius: 4px;
}

.email-text {
  color: #3b4648;
  font-weight: 400;
  font-family: "League Spartan", sans-serif;
}

.user-state {
  color: #98a2b3;
}

.total-amount {
  color: #3b4648;
  font-weight: 400;
  font-family: "League Spartan", sans-serif;
}

.quote-preview-dialog :deep(.v-overlay__scrim) {
  opacity: 0.55;
}

.quote-preview-dialog :deep(.v-overlay__content) {
  margin: 24px;
  max-height: calc(100vh - 48px);
}

.quote-preview-modal {
  position: relative;
  width: min(794px, calc(100vw - 48px));
  height: min(1123px, calc(100vh - 48px));
  background: #f5f6fa;
  border: 2px solid #3b4648;
  box-shadow: 0 24px 60px rgba(16, 24, 40, 0.22);
  overflow: hidden;
}

.quote-preview-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary-blue);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(16, 24, 40, 0.16);
}

.quote-preview-close:hover {
  background: #f2f3fb;
}

.quote-preview-document {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 32px 28px;
}

.quote-preview-page {
  display: block;
  width: 100%;
  height: auto;
  background: #ffffff;
  margin: 0 auto 24px;
  box-shadow: 0 1px 6px rgba(16, 24, 40, 0.12);
}

.quote-preview-page:last-child {
  margin-bottom: 0;
}

.quote-preview-state {
  width: 100%;
  height: 100%;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px;
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  text-align: center;
}

.quote-preview-state p {
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .filter-section .d-flex {
    flex-direction: column;
    gap: 16px;
  }

  .search-container {
    width: 100%;
  }

  .admin-header h1 {
    font-size: var(--fs-xxl);
  }

  .quote-log-container {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .admin-header h1 {
    font-size: var(--fs-xl);
  }

  .search-input {
    height: 48px;
    padding: 4px 12px;
  }

  .search-field {
    font-size: var(--fs-small);
  }
}
</style>
